/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { toast } from "sonner";
import axios from "axios";
import {
  Buildings, Plus, Trash, PencilSimple, X, FloppyDisk, Link as LinkIcon,
  UserPlus, UploadSimple, Copy, ArrowRight, Certificate, Users, ChartBar,
  CheckCircle, Clock, DownloadSimple, Sparkle,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLUE = "#0B2B66";
const GOLD = "#C9A227";

const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("rescigo_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return d || e?.message || "Error";
};

const CATEGORIES = [
  ["hospital", "Hospital"], ["clinic", "Clinic"], ["diagnostic_lab", "Diagnostic Lab"],
  ["medical_college", "Medical College"], ["ngo", "NGO"], ["government", "Government Body"],
  ["lsgd", "LSGD"], ["franchise", "Franchise"], ["waste_operator", "Waste Operator"],
  ["other", "Other"],
];
const TIERS = [
  ["basic", "Basic · Small clinic/institution · Shared LMS, standard branding"],
  ["professional", "Professional · Mid hospital/NGO · Co-branded page, custom branding, analytics"],
  ["enterprise", "Enterprise · Large/Govt deployments · White-label, regional dashboards, compliance integrations"],
];

const EMPTY = () => ({
  name: "", category: "hospital", tier: "basic",
  license_number: "", contact_person: "", contact_email: "", contact_phone: "",
  compliance_officer_name: "", compliance_officer_email: "", compliance_officer_phone: "",
  address: "", region: "",
  logo_url: "", partner_logo_1_url: "", partner_logo_2_url: "",
  primary_color: "#0B2B66", banner_url: "", intro_message: "", website: "",
  assigned_course_ids: [],
  use_custom_cert: false, custom_issuing_authority: "", custom_authority_seal_url: "",
  status: "active",
});

export default function TrainingPartners() {
  const [rows, setRows] = useState([]);
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [manageUsers, setManageUsers] = useState(null);
  const [progress, setProgress] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const [filterTier, setFilterTier] = useState("");

  const load = async () => {
    try {
      const [a, c] = await Promise.all([
        api.get(`/admin/training-partners${filterCat || filterTier
          ? `?${filterCat ? `category=${filterCat}` : ""}${filterCat && filterTier ? "&" : ""}${filterTier ? `tier=${filterTier}` : ""}`
          : ""}`),
        api.get("/admin/training/courses"),
      ]);
      setRows(a.data); setCourses(c.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, [filterCat, filterTier]);

  const save = async (data) => {
    try {
      // Backend EmailStr rejects "" — send null instead
      const payload = { ...data };
      for (const k of ["contact_email", "compliance_officer_email"]) {
        if (!payload[k]) payload[k] = null;
      }
      if (data.id) await api.put(`/admin/training-partners/${data.id}`, payload);
      else await api.post("/admin/training-partners", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this partner?")) return;
    try { await api.delete(`/admin/training-partners/${id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <AppShell>
      <div className="p-6 md:p-8 space-y-6" data-testid="training-partners">
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>Institutional Training Partners</div>
            <h1 className="font-heading text-3xl mt-1" style={{ color: BLUE }}>Training Partners</h1>
            <p className="text-sm text-[#5C5A55] mt-1">Onboard hospitals, clinics, NGOs, and government bodies as co-branded partners.</p>
          </div>
          <button onClick={() => setEditing(EMPTY())} data-testid="partner-new"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md text-white text-sm font-medium" style={{ background: BLUE }}>
            <Plus size={14}/> New Partner
          </button>
        </header>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <FS label="Category" value={filterCat} onChange={setFilterCat}
              opts={[["", "All categories"], ...CATEGORIES]} testid="filter-cat"/>
          <FS label="Tier" value={filterTier} onChange={setFilterTier}
              opts={[["", "All tiers"], ["basic", "Basic"], ["professional", "Professional"], ["enterprise", "Enterprise"]]}
              testid="filter-tier"/>
          <div className="text-xs text-[#5C5A55] self-end ml-auto">{rows.length} partner(s)</div>
        </div>

        <section className="bg-white rounded-xl border border-[#0B2B66]/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
                <th className="p-3 text-left font-semibold">Partner</th>
                <th className="p-3 text-left font-semibold">Category</th>
                <th className="p-3 text-left font-semibold">Tier</th>
                <th className="p-3 text-left font-semibold">Users</th>
                <th className="p-3 text-left font-semibold">Certs</th>
                <th className="p-3 text-left font-semibold">Public Page</th>
                <th className="p-3 text-left font-semibold">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-[#0B2B66]/5 hover:bg-[#0B2B66]/2" data-testid={`partner-row-${p.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-[10px] text-[#5C5A55]">{p.contact_email || p.license_number || "—"}</div>
                  </td>
                  <td className="p-3 text-xs uppercase tracking-[0.15em] text-[#5C5A55]">{(p.category || "—").replace(/_/g, " ")}</td>
                  <td className="p-3">
                    <TierPill tier={p.tier}/>
                  </td>
                  <td className="p-3 text-xs"><Users size={11} className="inline mr-1"/>{p.user_count ?? 0}</td>
                  <td className="p-3 text-xs"><Certificate size={11} className="inline mr-1"/>{p.certificate_count ?? 0}</td>
                  <td className="p-3 text-xs">
                    {p.slug ? (
                      <a href={`/training/hospital/${p.slug}`} target="_blank" rel="noreferrer"
                         className="text-[#0B2B66] hover:underline inline-flex items-center gap-1" data-testid={`partner-public-${p.id}`}>
                        /{p.slug} <ArrowRight size={9}/>
                      </a>
                    ) : <span className="text-[#5C5A55]">—</span>}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold ${p.status === "active" ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#F4C7B9] text-[#7A2E18]"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <IconBtn onClick={() => setProgress(p)} title="Progress & Certificates" testid={`partner-progress-${p.id}`}><ChartBar size={12}/></IconBtn>
                    <IconBtn onClick={() => setManageUsers(p)} title="Manage users" testid={`partner-users-${p.id}`}><UserPlus size={12}/></IconBtn>
                    <IconBtn onClick={() => setEditing(p)} title="Edit"><PencilSimple size={12}/></IconBtn>
                    {p.status === "active" && <IconBtn onClick={() => deactivate(p.id)} title="Deactivate" red><Trash size={12}/></IconBtn>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-sm text-[#5C5A55]">
                  No partners yet. Click <b>New Partner</b> to onboard your first institution.
                </td></tr>
              )}
            </tbody>
          </table>
        </section>

        {editing && <PartnerModal data={editing} courses={courses}
                                     onSave={save} onClose={() => setEditing(null)}/>}
        {manageUsers && <UsersModal partner={manageUsers}
                                       onClose={() => { setManageUsers(null); load(); }}/>}
        {progress && <ProgressModal partner={progress}
                                       onClose={() => { setProgress(null); load(); }}/>}
      </div>
    </AppShell>
  );
}

// ============================================================================
// Partner onboarding modal (5-step wizard)
// ============================================================================
function PartnerModal({ data, courses, onSave, onClose }) {
  const [f, setF] = useState(data);
  const [step, setStep] = useState(1);
  const steps = ["Profile", "Branding", "Compliance", "Courses", "Certificate", "Review"];
  const isEdit = !!f.id;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="partner-modal">
      <div className="bg-white rounded-xl w-full max-w-4xl shadow-2xl my-8 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#0B2B66]/10 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: GOLD }}>Partner Onboarding</div>
            <h2 className="font-heading text-xl mt-0.5" style={{ color: BLUE }}>{isEdit ? f.name : "New Training Partner"}</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded grid place-items-center hover:bg-[#0B2B66]/5"><X size={16}/></button>
        </div>

        {/* Stepper */}
        <div className="px-6 pt-4 flex items-center gap-2">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <button onClick={() => setStep(i + 1)}
                      className={`flex items-center gap-2 h-9 px-3 rounded-md text-xs font-medium transition ${
                        step === i + 1 ? "text-white" : "text-[#5C5A55] hover:text-[#0B2B66]"
                      }`} style={step === i + 1 ? { background: BLUE } : {}}
                      data-testid={`wizard-step-${i + 1}`}>
                <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-mono ${
                  step === i + 1 ? "bg-white/20" : "bg-[#0B2B66]/10"
                }`}>{i + 1}</span>
                {s}
              </button>
              {i < steps.length - 1 && <div className="flex-1 h-px bg-[#0B2B66]/10"/>}
            </React.Fragment>
          ))}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="p-6 space-y-5">
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <Fi label="Institution Name" v={f.name} on={(v) => setF({ ...f, name: v })} required testid="p-name"/>
                <FS label="Category" value={f.category} onChange={(v) => setF({ ...f, category: v })} opts={CATEGORIES} testid="p-cat"/>
                <Fi label="License / Registration No." v={f.license_number} on={(v) => setF({ ...f, license_number: v })}/>
                <FS label="Tier" value={f.tier} onChange={(v) => setF({ ...f, tier: v })} opts={TIERS} testid="p-tier"/>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Fi label="Contact Person" v={f.contact_person} on={(v) => setF({ ...f, contact_person: v })}/>
                <Fi label="Contact Email" type="email" v={f.contact_email} on={(v) => setF({ ...f, contact_email: v })}/>
                <Fi label="Contact Phone" v={f.contact_phone} on={(v) => setF({ ...f, contact_phone: v })}/>
                <Fi label="Website" v={f.website} on={(v) => setF({ ...f, website: v })}/>
                <Fi label="Address" v={f.address} on={(v) => setF({ ...f, address: v })}/>
                <Fi label="Region" v={f.region} on={(v) => setF({ ...f, region: v })}/>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-xs text-[#5C5A55] bg-[#FAFBFF] p-3 rounded border border-[#0B2B66]/10">
                Branding assets are surfaced on the co-branded hospital landing page at <b>/training/hospital/{data.slug || "<slug>"}</b> and on certificate PDFs.
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Fi label="Primary Logo URL" v={f.logo_url} on={(v) => setF({ ...f, logo_url: v })} placeholder="https://…/logo.png"/>
                <Fi label="Banner Image URL" v={f.banner_url} on={(v) => setF({ ...f, banner_url: v })}/>
                <Fi label="Partner Logo 1 URL" v={f.partner_logo_1_url} on={(v) => setF({ ...f, partner_logo_1_url: v })}/>
                <Fi label="Partner Logo 2 URL" v={f.partner_logo_2_url} on={(v) => setF({ ...f, partner_logo_2_url: v })}/>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={f.primary_color} onChange={(e) => setF({ ...f, primary_color: e.target.value })}
                           className="w-12 h-10 border border-[#0B2B66]/20 rounded cursor-pointer"/>
                    <input type="text" value={f.primary_color} onChange={(e) => setF({ ...f, primary_color: e.target.value })}
                           className="flex-1 h-10 px-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none font-mono"/>
                  </div>
                </div>
              </div>
              <FT label="Intro message (shown on hospital landing page)"
                   v={f.intro_message} on={(v) => setF({ ...f, intro_message: v })} rows={3}/>
            </div>
          )}
          {step === 3 && (
            <div className="grid md:grid-cols-2 gap-3">
              <Fi label="Compliance Officer Name" v={f.compliance_officer_name} on={(v) => setF({ ...f, compliance_officer_name: v })} testid="p-co-name"/>
              <Fi label="Compliance Officer Email" type="email" v={f.compliance_officer_email} on={(v) => setF({ ...f, compliance_officer_email: v })}/>
              <Fi label="Compliance Officer Phone" v={f.compliance_officer_phone} on={(v) => setF({ ...f, compliance_officer_phone: v })}/>
            </div>
          )}
          {step === 4 && (
            <div>
              <div className="text-xs text-[#5C5A55] mb-3">Select courses to assign to this partner. Leave empty to publish ALL active courses to their co-branded page.</div>
              <div className="border border-[#0B2B66]/15 rounded-md max-h-80 overflow-y-auto bg-[#FAFBFF]">
                {courses.length === 0 ? (
                  <div className="p-4 text-xs text-[#5C5A55] italic">No courses created yet. Go to Training Academy admin first.</div>
                ) : courses.map(c => (
                  <label key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-white border-b border-[#0B2B66]/5">
                    <input type="checkbox" checked={f.assigned_course_ids.includes(c.id)}
                           onChange={() => setF({
                             ...f,
                             assigned_course_ids: f.assigned_course_ids.includes(c.id)
                               ? f.assigned_course_ids.filter(x => x !== c.id)
                               : [...f.assigned_course_ids, c.id]
                           })}
                           className="accent-[#0B2B66]"/>
                    <div className="flex-1">
                      <div className="font-medium">{c.name}</div>
                      <div className="text-[10px] text-[#5C5A55]">{c.audience} · {c.duration_minutes}m · {c.is_paid ? `₹${c.price_paise/100}` : "Free"}</div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="text-[11px] text-[#5C5A55] mt-2">{f.assigned_course_ids.length} selected · {courses.length - f.assigned_course_ids.length} unassigned</div>
            </div>
          )}
          {step === 5 && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-[#FAFBFF] to-white border border-[#0B2B66]/15 rounded-lg p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>Custom Certificate Design</div>
                    <h3 className="font-heading text-lg mt-1" style={{ color: BLUE }}>Issue certificates under <b>{f.name || "your institution"}</b>'s authority</h3>
                    <p className="text-xs text-[#5C5A55] mt-2 leading-relaxed">
                      When enabled, certificates earned through your branded URL show your
                      institution's name as the <b>Issuing Authority</b> and your logo as the
                      <b> Authority Seal</b>. All other layout, typography, signatures, and
                      legal text inherit from the platform-wide design configured in
                      <span className="text-[#0B2B66] mx-1">LMS Configuration → Certificate Design</span>.
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer flex-none">
                    <input type="checkbox" checked={!!f.use_custom_cert}
                           onChange={(e) => setF({
                             ...f,
                             use_custom_cert: e.target.checked,
                             // auto-default the 2 fields when toggled on
                             custom_issuing_authority: e.target.checked && !f.custom_issuing_authority ? f.name : f.custom_issuing_authority,
                             custom_authority_seal_url: e.target.checked && !f.custom_authority_seal_url ? f.logo_url : f.custom_authority_seal_url,
                           })}
                           data-testid="cert-toggle"
                           className="sr-only peer"/>
                    <div className="w-11 h-6 bg-slate-300 rounded-full peer-checked:bg-[#2D5A27] transition relative
                                      after:content-[''] after:absolute after:top-0.5 after:left-0.5
                                      after:w-5 after:h-5 after:bg-white after:rounded-full after:transition
                                      peer-checked:after:translate-x-5"/>
                  </label>
                </div>
              </div>

              {f.use_custom_cert && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Fi label="Issuing Authority"
                        v={f.custom_issuing_authority}
                        on={(v) => setF({ ...f, custom_issuing_authority: v })}
                        placeholder={f.name || "e.g. Amrita Institute of Medical Sciences"}
                        testid="cert-issuing-authority"/>
                    <div className="text-[10px] text-[#5C5A55] mt-1">Defaults to the institution name. Shown as <b>"Issued by"</b> on the certificate.</div>
                  </div>
                  <div>
                    <Fi label="Authority Seal URL"
                        v={f.custom_authority_seal_url}
                        on={(v) => setF({ ...f, custom_authority_seal_url: v })}
                        placeholder={f.logo_url || "https://…/seal.png"}
                        testid="cert-seal-url"/>
                    <div className="text-[10px] text-[#5C5A55] mt-1">Defaults to the primary logo. Embossed near the signature line.</div>
                  </div>

                  {/* Live preview block */}
                  <div className="md:col-span-2 mt-4 p-5 rounded-lg border-2 border-dashed border-[#C9A227]/40 bg-white">
                    <div className="text-[9px] uppercase tracking-[0.25em] font-semibold text-[#C9A227] mb-3">Preview · how it appears on the cert</div>
                    <div className="flex items-center justify-between border-t border-b border-[#0B2B66]/10 py-4">
                      <div className="flex items-center gap-3">
                        {(f.custom_authority_seal_url || f.logo_url) ? (
                          <img src={f.custom_authority_seal_url || f.logo_url}
                               alt="seal"
                               className="w-14 h-14 object-contain bg-white rounded-full border border-[#0B2B66]/10 p-1.5"/>
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-slate-100 grid place-items-center"><Buildings size={20} className="text-slate-400"/></div>
                        )}
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.22em] text-[#5C5A55]">Issued by</div>
                          <div className="font-heading text-base mt-0.5" style={{ color: BLUE }}>
                            {f.custom_issuing_authority || f.name || "Your Institution"}
                          </div>
                        </div>
                      </div>
                      <Certificate size={28} weight="duotone" className="text-[#C9A227] opacity-50"/>
                    </div>
                    <div className="text-[10px] text-[#5C5A55] mt-3 italic">
                      Layout · signatures · numbering format · validity period are all inherited from
                      the main <b>LMS Configuration → Certificate Design</b> settings — only these 2
                      fields are customized per partner.
                    </div>
                  </div>
                </div>
              )}

              {!f.use_custom_cert && (
                <div className="text-xs text-[#5C5A55] bg-slate-50 border border-slate-200 rounded p-3">
                  When disabled, certificates earned via this partner's URL still get the
                  partner logo as a co-brand mark, but the issuing authority remains
                  Rescigo Health (the platform default).
                </div>
              )}
            </div>
          )}

          {step === 6 && (
            <div className="space-y-3">
              <div className="bg-[#FAFBFF] border border-[#0B2B66]/10 rounded-lg p-4">
                <h3 className="font-heading text-lg mb-3" style={{ color: BLUE }}>{f.name || "(Untitled)"}</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <KV k="Category" v={(f.category || "—").replace(/_/g, " ")}/>
                  <KV k="Tier" v={f.tier}/>
                  <KV k="License #" v={f.license_number || "—"}/>
                  <KV k="Contact" v={`${f.contact_person || "—"} · ${f.contact_email || "—"}`}/>
                  <KV k="Compliance Officer" v={f.compliance_officer_name || "—"}/>
                  <KV k="Region" v={f.region || "—"}/>
                  <KV k="Primary Color" v={<span style={{ background: f.primary_color, color: "#fff", padding: "2px 8px", borderRadius: 4 }} className="font-mono text-xs">{f.primary_color}</span>}/>
                  <KV k="Assigned Courses" v={`${f.assigned_course_ids.length} of ${courses.length}`}/>
                  <KV k="Custom Certificate" v={f.use_custom_cert
                    ? <span className="text-emerald-700 font-semibold">Enabled · {f.custom_issuing_authority || f.name}</span>
                    : <span className="text-[#5C5A55]">Default (Rescigo authority)</span>}/>
                </div>
              </div>
              {!isEdit && (
                <div className="text-xs text-[#5C5A55]">
                  After saving, use the <b>Manage Users</b> action to seed the partner admin account — they'll receive a welcome email with a one-time activation link.
                </div>
              )}
            </div>
          )}

          {/* Footer nav */}
          <div className="flex items-center gap-3 pt-4 border-t border-[#0B2B66]/10">
            {step > 1 && (
              <button type="button" onClick={() => setStep(step - 1)}
                      className="h-11 px-4 text-sm text-[#5C5A55] hover:text-[#0B2B66]">← Back</button>
            )}
            <div className="flex-1"/>
            {step < steps.length ? (
              <button type="button" onClick={() => setStep(step + 1)}
                      className="h-11 px-6 rounded-md text-white text-sm font-medium inline-flex items-center gap-2" style={{ background: BLUE }}>
                Next <ArrowRight size={12} weight="bold"/>
              </button>
            ) : (
              <button type="submit" data-testid="partner-save"
                      className="h-11 px-6 rounded-md text-white text-sm font-medium inline-flex items-center gap-2" style={{ background: BLUE }}>
                <FloppyDisk size={14} weight="fill"/> {isEdit ? "Save changes" : "Create partner"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Partner users modal — list + seed admin + CSV upload
// ============================================================================
function UsersModal({ partner, onClose }) {
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState(null);
  const [csvResult, setCsvResult] = useState(null);

  const load = async () => {
    try { const r = await api.get(`/admin/training-partners/${partner.id}/users`); setUsers(r.data); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const seedAdmin = async (body) => {
    try {
      const isTrainee = body.role === "partner_learner";
      const url = isTrainee
        ? `/admin/training-partners/${partner.id}/invite-trainee`
        : `/admin/training-partners/${partner.id}/seed-admin`;
      const payload = isTrainee
        ? { email: body.email, name: body.name, phone: body.phone || null }
        : body;
      const r = await api.post(url, payload);
      toast.success(isTrainee
        ? `Course invitation sent to ${body.email}`
        : `Admin user seeded · activation link sent to ${body.email}`);
      setAdding(null); load();
      if (r.data.activate_url) {
        if (confirm(`One-time link (also emailed):\n\n${r.data.activate_url}\n\nCopy to clipboard?`)) {
          navigator.clipboard.writeText(r.data.activate_url);
        }
      }
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("default_role", "partner_learner");
    try {
      const r = await api.post(`/admin/training-partners/${partner.id}/users/csv`, form,
                                  { headers: { "Content-Type": "multipart/form-data" } });
      setCsvResult(r.data);
      toast.success(`${r.data.created} user(s) created · ${r.data.skipped_existing} skipped`);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    e.target.value = "";
  };

  const deactivate = async (uid) => {
    if (!window.confirm("Deactivate this user?")) return;
    try { await api.delete(`/admin/training-partners/${partner.id}/users/${uid}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="users-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl shadow-2xl my-8 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#0B2B66]/10 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: GOLD }}>Partner Users</div>
            <h2 className="font-heading text-xl mt-0.5" style={{ color: BLUE }}>{partner.name}</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded grid place-items-center hover:bg-[#0B2B66]/5"><X size={16}/></button>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button onClick={() => setAdding({ email: "", name: "", phone: "", role: "partner_learner" })}
                    data-testid="partner-trainee-add"
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-md text-white text-xs font-medium bg-[#2D5A27] hover:bg-[#234920]">
              <Plus size={11}/> Invite trainee to course
            </button>
            <button onClick={() => setAdding({ email: "", name: "", phone: "", role: "partner_admin" })}
                    data-testid="partner-user-add"
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-md text-xs font-medium border border-[#0B2B66]/20 text-[#0B2B66] hover:bg-[#0B2B66]/5">
              <UserPlus size={11}/> Seed admin user
            </button>
            <label className="inline-flex items-center gap-2 h-9 px-4 rounded-md text-xs font-medium border border-[#0B2B66]/20 text-[#0B2B66] cursor-pointer hover:bg-[#0B2B66]/5"
                   data-testid="partner-user-csv">
              <UploadSimple size={11}/> Bulk upload CSV
              <input type="file" accept=".csv" className="hidden" onChange={uploadCsv}/>
            </label>
            <a href={`${API}/admin/training-partners/${partner.id}/users/template.csv`}
               target="_blank" rel="noreferrer"
               onClick={async (e) => {
                 e.preventDefault();
                 const r = await api.get(`/admin/training-partners/${partner.id}/users/template.csv`, { responseType: "blob" });
                 const url = URL.createObjectURL(r.data);
                 const a = document.createElement("a"); a.href = url; a.download = "rescigo-partner-users-template.csv"; a.click();
                 URL.revokeObjectURL(url);
               }}
               className="text-xs text-[#5C5A55] hover:text-[#0B2B66] inline-flex items-center gap-1">
              <Copy size={10}/> Download template
            </a>
          </div>
          {csvResult && (
            <div className="text-xs bg-[#FAFBFF] p-3 rounded border border-[#0B2B66]/10 mb-4">
              ✓ <b>{csvResult.created}</b> created · <b>{csvResult.skipped_existing}</b> duplicates skipped · <b>{csvResult.error_count}</b> errors
              {csvResult.error_count > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[#5C5A55]">View errors</summary>
                  <ul className="mt-2 space-y-1 font-mono">
                    {csvResult.errors.map((e, i) => <li key={i}>Line {e.line}: {e.error}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">Email</th>
                <th className="p-2 text-left">Role</th>
                <th className="p-2 text-left">Department</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-[#0B2B66]/5 text-xs" data-testid={`puser-${u.id}`}>
                  <td className="p-2 font-medium">{u.name}</td>
                  <td className="p-2">{u.email}</td>
                  <td className="p-2">
                    <span className="px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold bg-[#EAF1FD] text-[#0B2B66]">
                      {(u.role || "").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="p-2">{u.department || "—"}</td>
                  <td className="p-2">
                    <span className={`px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold ${u.status === "active" ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#F4C7B9] text-[#7A2E18]"}`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="p-2 text-right">
                    {u.status === "active" && <IconBtn onClick={() => deactivate(u.id)} red title="Deactivate"><Trash size={10}/></IconBtn>}
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-[#5C5A55] italic">No users yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {adding && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            {adding.role === "partner_learner" ? (
              <>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#2D5A27] mb-1">Invite Trainee</div>
                <h3 className="font-heading text-lg mb-1" style={{ color: BLUE }}>Send a course invitation</h3>
                <p className="text-xs text-[#5C5A55] mb-4">They'll receive an attractive welcome email and land directly on the course catalog when they click the activation link — never on the partner dashboard.</p>
              </>
            ) : (
              <h3 className="font-heading text-lg mb-4" style={{ color: BLUE }}>Seed admin user</h3>
            )}
            <form onSubmit={(e) => { e.preventDefault(); seedAdmin(adding); }} className="space-y-3">
              <Fi label="Name" v={adding.name} on={(v) => setAdding({ ...adding, name: v })} required testid="seed-name"/>
              <Fi label="Email" type="email" v={adding.email} on={(v) => setAdding({ ...adding, email: v })} required testid="seed-email"/>
              <Fi label="Phone" v={adding.phone} on={(v) => setAdding({ ...adding, phone: v })}/>
              {adding.role !== "partner_learner" && (
                <FS label="Role" value={adding.role} onChange={(v) => setAdding({ ...adding, role: v })}
                    opts={[["partner_admin", "Partner Admin"], ["partner_manager", "Partner Manager"]]}/>
              )}
              <div className="flex items-center gap-2 pt-2 border-t border-[#0B2B66]/10">
                <button type="submit" data-testid="seed-save"
                        className="h-10 px-4 rounded-md text-white text-sm font-medium"
                        style={{ background: adding.role === "partner_learner" ? "#2D5A27" : BLUE }}>
                  {adding.role === "partner_learner" ? "Send course invitation" : "Seed & send magic link"}
                </button>
                <button type="button" onClick={() => setAdding(null)} className="h-10 px-3 text-sm text-[#5C5A55]">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Primitives
// ============================================================================
const TIER_META = {
  basic: { bg: "#E5E2D9", color: "#5C5A55", label: "Basic" },
  professional: { bg: "#EAF1FD", color: "#0B2B66", label: "Pro" },
  enterprise: { bg: "#F9E4B7", color: "#7A5A11", label: "Enterprise" },
};
const TierPill = ({ tier }) => {
  const m = TIER_META[tier] || TIER_META.basic;
  return <span className="px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold"
                 style={{ background: m.bg, color: m.color }}>{m.label}</span>;
};
const IconBtn = ({ onClick, children, title, red, testid }) => (
  <button type="button" onClick={onClick} title={title} data-testid={testid}
          className={`w-7 h-7 rounded inline-flex items-center justify-center ml-1 ${red ? "hover:bg-red-50 text-[#5C5A55] hover:text-red-600" : "hover:bg-[#0B2B66]/10 text-[#5C5A55] hover:text-[#0B2B66]"}`}>
    {children}
  </button>
);
const Fi = ({ label, v, on, type = "text", required, placeholder, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">{label}</label>
    <input type={type} value={v || ""} onChange={(e) => on(e.target.value)}
           required={required} placeholder={placeholder} data-testid={testid}
           className="w-full h-10 px-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66]"/>
  </div>
);
const FT = ({ label, v, on, rows = 2 }) => (
  <div>
    {label && <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">{label}</label>}
    <textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={rows}
              className="w-full px-3 py-2 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66]"/>
  </div>
);
const FS = ({ label, value, onChange, opts, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">{label}</label>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="h-10 px-3 border border-[#0B2B66]/20 rounded-md text-sm bg-white outline-none focus:border-[#0B2B66] min-w-[160px]">
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);
const KV = ({ k, v }) => (
  <div><div className="text-[9px] uppercase tracking-[0.18em] text-[#5C5A55]">{k}</div>
    <div className="mt-0.5 font-medium text-sm">{v}</div></div>
);


// ============================================================================
// Progress & Certificates Modal
// ============================================================================
function ProgressModal({ partner, onClose }) {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | with_cert | in_progress | external
  const [expand, setExpand] = useState(null);

  useEffect(() => {
    api.get(`/admin/training-partners/${partner.id}/progress`)
      .then(r => setData(r.data))
      .catch(e => toast.error(fmtErr(e)));
  }, [partner.id]);

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const headers = ["Name", "Email", "Role", "Status", "External", "In Progress", "Passed", "Certs", "Latest Cert No"];
    const rows = data.rows.map(r => [
      r.name, r.email, r.role, r.status, r.external ? "Yes" : "No",
      r.stats.in_progress, r.stats.passed, r.stats.cert_count,
      (r.certificates[0]?.certificate_no) || "",
    ]);
    const csv = [headers, ...rows].map(r =>
      r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${partner.slug}-progress-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const filtered = (data?.rows || []).filter(r => {
    if (filter === "with_cert" && r.stats.cert_count === 0) return false;
    if (filter === "in_progress" && r.stats.in_progress === 0) return false;
    if (filter === "external" && !r.external) return false;
    if (search) {
      const s = search.toLowerCase();
      return r.name.toLowerCase().includes(s) || r.email.toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="progress-modal">
      <div className="bg-white rounded-xl w-full max-w-5xl my-6 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#0B2B66]/10 sticky top-0 bg-white z-10 rounded-t-xl">
          <div className="flex items-center gap-3">
            {partner.logo_url
              ? <img src={partner.logo_url} alt="" className="w-10 h-10 rounded object-contain bg-white border border-[#0B2B66]/10"/>
              : <Buildings size={24} className="text-[#0B2B66]"/>}
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>Progress & Certificates</div>
              <h2 className="font-heading text-xl mt-0.5" style={{ color: BLUE }}>{partner.name}</h2>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded grid place-items-center hover:bg-slate-100">
            <X size={16}/>
          </button>
        </div>

        {!data ? (
          <div className="p-16 text-center text-sm text-[#5C5A55]">Loading…</div>
        ) : (
          <div className="p-6 space-y-5">
            {/* KPI strip */}
            <div className="grid grid-cols-4 gap-3">
              <PStat label="Users" v={data.totals.users} Icon={Users} color="#0B2B66"/>
              <PStat label="In progress" v={data.totals.in_progress} Icon={Clock} color="#FF8A00"/>
              <PStat label="Passed" v={data.totals.passed} Icon={CheckCircle} color="#2D5A27"/>
              <PStat label="Certificates" v={data.totals.certs} Icon={Certificate} color="#C9A227"/>
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder="Search by name or email…"
                       data-testid="progress-search"
                       className="w-full h-9 px-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66]"/>
              </div>
              {[
                ["all", `All (${data.rows.length})`],
                ["with_cert", "With certificate"],
                ["in_progress", "In progress"],
                ["external", "External (via partner URL)"],
              ].map(([v, l]) => (
                <button key={v} onClick={() => setFilter(v)}
                        data-testid={`progress-filter-${v}`}
                        className={`h-8 px-3 rounded-full text-xs font-medium ${
                          filter === v ? "bg-[#0B2B66] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}>
                  {l}
                </button>
              ))}
              <div className="ml-auto"/>
              <button onClick={exportCsv} disabled={!data.rows.length}
                      data-testid="progress-export"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium disabled:opacity-50">
                <DownloadSimple size={11}/> Export CSV
              </button>
            </div>

            {/* Table */}
            <div className="border border-[#0B2B66]/10 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-[9px] uppercase tracking-[0.18em] text-[#5C5A55]">
                    <th className="p-3 text-left font-semibold">User</th>
                    <th className="p-3 text-left font-semibold">Role</th>
                    <th className="p-3 text-center font-semibold">In Progress</th>
                    <th className="p-3 text-center font-semibold">Passed</th>
                    <th className="p-3 text-center font-semibold">Certs</th>
                    <th className="p-3 text-left font-semibold">Latest activity</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => {
                    const lastCert = r.certificates[0];
                    const lastEnr = r.enrollments[0];
                    const isOpen = expand === r.user_id;
                    return (
                      <React.Fragment key={r.user_id}>
                        <tr className="border-t border-[#0B2B66]/5 hover:bg-slate-50/60"
                            data-testid={`progress-row-${r.user_id}`}>
                          <td className="p-3">
                            <div className="font-medium text-slate-900 flex items-center gap-2">
                              {r.name}
                              {r.external && <span title="Earned cert via your partner URL — not registered as a partner user"
                                                     className="text-[8px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 uppercase tracking-[0.15em] font-bold inline-flex items-center gap-0.5">
                                <Sparkle size={8} weight="fill"/> External
                              </span>}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono">{r.email}</div>
                          </td>
                          <td className="p-3 text-xs uppercase tracking-[0.15em] text-[#5C5A55]">{r.role.replace(/_/g, " ")}</td>
                          <td className="p-3 text-center text-xs">{r.stats.in_progress || "—"}</td>
                          <td className="p-3 text-center text-xs">{r.stats.passed || "—"}</td>
                          <td className="p-3 text-center text-xs font-semibold" style={{ color: r.stats.cert_count ? "#2D5A27" : "#94A3B8" }}>
                            {r.stats.cert_count || "—"}
                          </td>
                          <td className="p-3 text-[11px] text-slate-600">
                            {lastCert ? `Issued ${new Date(lastCert.issued_at).toLocaleDateString()}`
                              : lastEnr ? `${lastEnr.status === "in_progress" ? "Started" : "Completed"} ${new Date(lastEnr.started_at || lastEnr.completed_at).toLocaleDateString()}`
                              : "No activity"}
                          </td>
                          <td className="p-3 text-right">
                            {(r.enrollments.length > 0 || r.certificates.length > 0) && (
                              <button onClick={() => setExpand(isOpen ? null : r.user_id)}
                                      className="text-[10px] uppercase tracking-[0.15em] text-[#0B2B66] hover:underline font-semibold">
                                {isOpen ? "Hide" : "Details"}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50/40">
                            <td colSpan={7} className="p-4">
                              <div className="grid md:grid-cols-2 gap-5">
                                <div>
                                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5C5A55] mb-2">Enrollments</div>
                                  {r.enrollments.length === 0
                                    ? <div className="text-xs text-slate-500 italic">No enrollments yet.</div>
                                    : <ul className="space-y-1.5">
                                        {r.enrollments.map(e => (
                                          <li key={e.id} className="text-xs flex items-center gap-2">
                                            <StatusDot s={e.status}/>
                                            <span className="flex-1 truncate">{e.course_name || "—"}</span>
                                            <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{e.status.replace(/_/g, " ")}</span>
                                          </li>
                                        ))}
                                      </ul>}
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5C5A55] mb-2">Certificates ({r.certificates.length})</div>
                                  {r.certificates.length === 0
                                    ? <div className="text-xs text-slate-500 italic">No certificates yet.</div>
                                    : <ul className="space-y-1.5">
                                        {r.certificates.map(c => (
                                          <li key={c.certificate_no} className="text-xs flex items-center gap-2">
                                            <Certificate size={11} weight="duotone" className="text-[#C9A227]"/>
                                            <span className="flex-1 truncate">{c.course_name || c.cert_type}</span>
                                            <span className="font-mono text-[10px] text-slate-500">{c.certificate_no}</span>
                                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/training/certificate/${c.certificate_no}.pdf`}
                                               target="_blank" rel="noreferrer"
                                               data-testid={`cert-pdf-${c.certificate_no}`}
                                               className="text-[10px] text-[#0B2B66] hover:underline inline-flex items-center gap-0.5">
                                              <DownloadSimple size={10}/> PDF
                                            </a>
                                          </li>
                                        ))}
                                      </ul>}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-12 text-sm text-[#5C5A55] italic">
                      No users match this filter.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="text-[11px] text-[#5C5A55] flex items-center gap-2 pt-2">
              <Sparkle size={11} weight="fill" className="text-[#C9A227]"/>
              External users earned certificates by signing in through{" "}
              <a href={`/training/hospital/${partner.slug}/login`} target="_blank" rel="noreferrer"
                 className="text-[#0B2B66] underline">your branded login</a>.
              Their certificates are auto co-branded with your institutional logo.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const PStat = ({ label, v, Icon, color }) => (
  <div className="bg-white border border-[#0B2B66]/10 rounded-lg p-4">
    <div className="flex items-center justify-between">
      <div className="text-[9px] uppercase tracking-[0.2em] font-semibold text-[#5C5A55]">{label}</div>
      <Icon size={14} weight="duotone" style={{ color }}/>
    </div>
    <div className="font-heading text-3xl mt-1.5" style={{ color }}>{v}</div>
  </div>
);

const StatusDot = ({ s }) => {
  const c = s === "passed" ? "#2D5A27" : s === "in_progress" ? "#FF8A00" : "#94A3B8";
  return <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: c, boxShadow: `0 0 0 3px ${c}20` }}/>;
};

